<template>
  <div class="payment-page">
    <AppHeader />
    <div class="payment-container">
      <div v-if="loading" class="payment-form loading-state">
        <div class="loading-spinner"></div>
         <p>{{ $t('legacy.Подготовка платежа...') }}</p>
      </div>

      <div v-else-if="error" class="payment-result error">
        <div class="result-icon">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#F44336"/>
            <path d="M15 9L9 15M9 9L15 15" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
           <h2>{{ $t('common.error') }}</h2>
        <p>{{ error }}</p>
        <button class="retry-btn" @click="initPayment">
           {{ $t('legacy.Попробовать снова') }}
        </button>
        <router-link to="/subscription/plans" class="back-btn">
           {{ $t('legacy.Вернуться к тарифам') }}
        </router-link>
      </div>

      <div v-else class="payment-form">
        <div class="payment-header">
           <h1>{{ $t('legacy.Оплата подписки') }}</h1>
           <p>{{ $t('legacy.dynamic.selectedPlan', { plan: getPlanDisplayName(plan) }) }}</p>
          <p class="payment-amount">
             {{ $t('legacy.Сумма к оплате:') }}
            <strong v-if="paymentQuote">
              <span v-if="Number(paymentQuote.originalAmount) > Number(paymentQuote.amount)" class="original-price">
                {{ formatQuoteAmount(paymentQuote.originalAmount, paymentQuote.currency) }}
              </span>
              {{ formatQuoteAmount(paymentQuote.amount, paymentQuote.currency) }}
            </strong>
            <span v-else-if="quoteLoading">{{ $t('legacy.paymentQuoteLoading') }}</span>
            <span v-else-if="quoteError" class="quote-error">{{ quoteError }}</span>
            <span v-else>—</span>
          </p>
        </div>

        <div class="promo-section">
          <div class="promo-input-group">
            <input 
              v-model="promoCode" 
              type="text" 
               :placeholder="$t('legacy.Промо-код')"
              :disabled="appliedPromo"
              class="promo-input"
            />
            <button 
              v-if="!appliedPromo"
              @click="applyPromoCode" 
              :disabled="!promoCode || validatingPromo"
              class="promo-btn"
            >
               {{ validatingPromo ? '...' : $t('legacy.Применить') }}
            </button>
            <button 
              v-else
              @click="removePromoCode" 
              class="promo-btn promo-btn-remove"
            >
               {{ $t('common.cancel') }}
            </button>
          </div>
          <div v-if="appliedPromo" class="promo-applied">
            <span v-if="appliedPromo.type === 'discount'" class="promo-badge discount">
               {{ $t('legacy.dynamic.discountApplied', { percent: appliedPromo.discountPercent }) }}
            </span>
            <span v-else class="promo-badge trial">
               {{ $t('legacy.dynamic.trialApplied', { days: appliedPromo.trialDays, plan: appliedPromo.trialPlan === 'base' ? $t('legacy.Базовый') : $t('legacy.Премиум') }) }}
            </span>
          </div>
          <div v-if="promoError" class="promo-error">{{ promoError }}</div>
        </div>

        <div class="provider-section">
          <h3>{{ $t('legacy.paymentProviderTitle') }}</h3>
          <p class="provider-hint">{{ $t('legacy.paymentProviderHint') }}</p>
          <label class="provider-option" :class="{ selected: provider === 'yookassa' }">
            <input v-model="provider" type="radio" value="yookassa" :disabled="providersLoading || !!providersError" @change="loadPaymentQuote" />
            <span>
              <strong>{{ $t('legacy.paymentProviderRussia') }}</strong>
              <small>{{ $t('legacy.paymentProviderYooKassa') }}</small>
            </span>
          </label>
          <label class="provider-option" :class="{ selected: provider === 'prodamus', unavailable: !providers.prodamus }">
            <input v-model="provider" type="radio" value="prodamus" :disabled="providersLoading || !!providersError || !providers.prodamus" @change="loadPaymentQuote" />
            <span>
              <strong>{{ $t('legacy.paymentProviderOutsideRussia') }}</strong>
              <small v-if="providers.prodamus">
                {{ $t('legacy.paymentProviderProdamus') }}<span v-if="prodamusCurrency"> · {{ prodamusCurrency }}</span>
              </small>
              <small v-else>{{ $t('legacy.paymentProviderUnavailable') }}</small>
            </span>
          </label>
          <p v-if="providersLoading" class="provider-state">{{ $t('legacy.paymentProvidersLoading') }}</p>
          <p v-else-if="providersError" class="provider-error">{{ providersError }}</p>
        </div>

        <div v-if="provider" class="payment-info">
          <div class="info-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#496782" stroke-width="2"/>
              <path d="M12 8V12M12 16H12.01" stroke="#496782" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </div>
           <span>{{ provider === 'prodamus' ? $t('legacy.redirectToProdamus') : $t('legacy.Вы будете перенаправлены на защищённую страницу оплаты ЮКасса') }}</span>
        </div>

        <div v-if="paymentQuote && !paymentQuote.trial && provider !== 'prodamus'" class="subscription-agreement">
          <div class="agreement-amount-info">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style="flex-shrink:0;margin-top:1px">
              <circle cx="12" cy="12" r="10" stroke="#496782" stroke-width="2"/>
              <path d="M12 8V12M12 16H12.01" stroke="#496782" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <span>
              {{ $t('legacy.Ежемесячное автосписание:') }}
              <strong>{{ formatQuoteAmount(paymentQuote.amount, paymentQuote.currency) }}</strong>
              {{ $t('— карта будет сохранена для автоматического продления. Автопродление можно отменить в любой момент в разделе «Моя подписка».') }}
            </span>
          </div>
        </div>

        <button 
          class="pay-btn" 
          @click="initPayment"
          :disabled="processing || providersLoading || !!providersError || !provider || !paymentQuote || quoteLoading || !!quoteError || (provider === 'prodamus' && !providers.prodamus)"
        >
           {{ processing ? $t('legacy.Создание платежа...') : $t('legacy.Перейти к оплате') }}
        </button>

        <div class="back-link">
           <router-link to="/subscription/plans">← {{ $t('legacy.backToPlanChoice') }}</router-link>
        </div>

        <div class="payment-security">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="11" width="18" height="11" rx="2" stroke="#999" stroke-width="2"/>
            <path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11" stroke="#999" stroke-width="2"/>
          </svg>
           <span>{{ provider === 'prodamus' ? $t('legacy.paymentProviderProdamus') : $t('legacy.Безопасная оплата через ЮКасса') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue';
import { createPayment, getPaymentProviders, getPaymentQuote } from '@/api/payments';
import { validatePromoCode } from '@/api/ambassadors';
import { useToast } from 'vue-toastification';
import { mapGetters } from 'vuex';
import { paymentErrorMessage, paymentResponseError } from '@/utils/paymentError';

export default {
  name: 'SubscriptionPaymentPage',
  components: {
    AppHeader,
  },
  data() {
    return {
      plan: null,
      price: null,
      loading: false,
      processing: false,
      error: null,
      toast: null,
      promoCode: '',
      appliedPromo: null,
      validatingPromo: false,
      promoError: null,
      providers: { prodamus: false },
      prodamusCurrency: null,
      provider: null,
      providersLoading: true,
      providersError: null,
      paymentQuote: null,
      quoteLoading: false,
      quoteError: null,
      quoteRequestSequence: 0,
    };
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated']),
    ...mapGetters('subscription', ['subscriptionType', 'subscriptionEndDate']),
  },
  async created() {
    this.toast = useToast();
    
    if (!this.isAuthenticated) {
      const plan = this.$route.query.plan;
      const price = this.$route.query.price;
      if (plan) {
        localStorage.setItem('pendingPlan', plan);
        if (price) {
          localStorage.setItem('pendingPlanPrice', price);
        }
      }
      this.$router.push('/login');
      return;
    }
    
    this.plan = this.$route.query.plan;
    this.price = this.$route.query.price;
    
    if (!this.plan) {
      this.$router.push('/subscription/plans');
      return;
    }
    
    if (this.isDowngrade()) {
       const endDate = this.subscriptionEndDate
         ? this.$formatDate(this.subscriptionEndDate)
        : '';
       this.error = this.$t('legacy.dynamic.downgradeError', {
         plan: this.getPlanDisplayName(this.subscriptionType),
         date: endDate
       });
    }
    await this.loadPaymentProviders();
  },
  methods: {
    async loadPaymentProviders() {
      this.providersLoading = true;
      this.providersError = null;
      try {
        const result = await getPaymentProviders();
        this.providers = { prodamus: result?.prodamus?.available === true };
        this.prodamusCurrency = result?.prodamus?.currency || null;
      } catch (error) {
        this.providersError = paymentResponseError(error, this.$t.bind(this), 'legacy.paymentProvidersError');
      } finally {
        this.providersLoading = false;
      }
    },
    async loadPaymentQuote() {
      const requestSequence = ++this.quoteRequestSequence;
      this.paymentQuote = null;
      this.quoteError = null;
      if (!this.plan || !this.provider || (this.provider === 'prodamus' && !this.providers.prodamus)) {
        this.quoteLoading = false;
        return;
      }

      this.quoteLoading = true;
      try {
        const promoCodeValue = this.appliedPromo ? this.promoCode.trim() : null;
        const quote = await getPaymentQuote(this.plan, this.provider, promoCodeValue);
        if (requestSequence === this.quoteRequestSequence) {
          this.paymentQuote = quote;
        }
      } catch (error) {
        if (requestSequence === this.quoteRequestSequence) {
          this.quoteError = paymentResponseError(error, this.$t.bind(this), 'legacy.paymentQuoteError');
        }
      } finally {
        if (requestSequence === this.quoteRequestSequence) {
          this.quoteLoading = false;
        }
      }
    },
    formatQuoteAmount(amount, currency) {
      if (amount === undefined || amount === null || !currency) return '—';
      return this.$formatCurrency(Number(amount), currency, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    getPlanRank(plan) {
      const ranks = { free: 0, base: 1, premium: 2, paid: 2 };
      return ranks[plan] || 0;
    },
    isDowngrade() {
      const currentRank = this.getPlanRank(this.subscriptionType);
      const selectedRank = this.getPlanRank(this.plan);
      return currentRank > 0 && selectedRank < currentRank;
    },
    getPlanDisplayName(plan) {
      const names = {
         free: this.$t('legacy.Бесплатный'),
         base: this.$t('legacy.Базовый'),
         premium: this.$t('legacy.Премиум'),
         paid: this.$t('legacy.Премиум')
      };
      return names[plan] || plan;
    },
    async applyPromoCode() {
      if (!this.promoCode || this.validatingPromo) return;
      
      this.validatingPromo = true;
      this.promoError = null;
      
      try {
        const result = await validatePromoCode(this.promoCode.trim());
        if (result.valid) {
          this.appliedPromo = result;
          await this.loadPaymentQuote();
           this.toast.success(this.$t('legacy.Промо-код применён!'));
        }
      } catch (err) {
         this.promoError = paymentResponseError(err, this.$t.bind(this), 'legacy.Промо-код недействителен');
      } finally {
        this.validatingPromo = false;
      }
    },
    removePromoCode() {
      this.appliedPromo = null;
      this.promoCode = '';
      this.promoError = null;
      this.loadPaymentQuote();
    },
    async initPayment() {
      if (this.processing || !this.provider || !this.paymentQuote || this.quoteLoading || this.quoteError || (this.provider === 'prodamus' && !this.providers.prodamus)) return;
      
      this.processing = true;
      this.error = null;
      
      try {
        const returnUrl = window.location.origin + '/payment/return';
        const promoCodeValue = this.appliedPromo ? this.promoCode.trim() : null;
        const response = await createPayment(this.plan, this.paymentQuote.amount, returnUrl, promoCodeValue, this.provider);
        
        if (response.trial) {
          this.toast.success(response.message);
          this.$router.push('/personal-subscription');
          return;
        }
        
        if (response.confirmationUrl) {
          window.location.href = response.confirmationUrl;
        } else {
           this.error = this.$t('legacy.Не удалось получить ссылку на оплату');
        }
      } catch (err) {
        this.error = paymentErrorMessage(err, this.$t.bind(this), this.provider);
        this.toast.error(this.error);
      } finally {
        this.processing = false;
      }
    }
  }
};
</script>

<style scoped>
.payment-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa, #e8ecef);
}

.payment-container {
  max-width: 500px;
  margin: 0 auto;
  padding: 40px 20px;
}

.payment-form {
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.loading-state {
  text-align: center;
  padding: 48px 32px;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e0e0e0;
  border-top-color: #496782;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-state p {
  color: #666;
  font-size: 16px;
  margin: 0;
}

.payment-header {
  text-align: center;
  margin-bottom: 32px;
}

.payment-header h1 {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin: 0 0 12px 0;
}

.payment-header p {
  font-size: 16px;
  color: #666;
  margin: 0 0 8px 0;
}

.payment-amount {
  font-size: 18px;
}

.payment-amount strong {
  color: #496782;
}

.original-price {
  text-decoration: line-through;
  color: #999;
  font-weight: 400;
  margin-right: 8px;
}

.promo-section {
  margin-bottom: 24px;
}

.promo-input-group {
  display: flex;
  gap: 8px;
}

.promo-input {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
}

.promo-input:focus {
  outline: none;
  border-color: #496782;
}

.promo-input:disabled {
  background: #f5f5f5;
  color: #666;
}

.promo-btn {
  padding: 12px 20px;
  background: #496782;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
}

.promo-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.promo-btn-remove {
  background: #e74c3c;
}

.promo-applied {
  margin-top: 8px;
}

.promo-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
}

.promo-badge.discount {
  background: #e8f5e9;
  color: #2e7d32;
}

.promo-badge.trial {
  background: #e3f2fd;
  color: #1565c0;
}

.promo-error {
  margin-top: 8px;
  color: #e74c3c;
  font-size: 13px;
}

.provider-section {
  margin: 0 0 20px;
}

.provider-section h3 {
  margin: 0 0 6px;
  color: #333;
  font-size: 16px;
}

.provider-hint,
.provider-state {
  margin: 0 0 10px;
  color: #666;
  font-size: 13px;
}

.provider-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  margin-top: 8px;
  border: 1px solid #ddd;
  border-radius: 8px;
  cursor: pointer;
}

.provider-option.selected {
  border-color: #496782;
  background: #f0f7ff;
}

.provider-option.unavailable {
  opacity: 0.55;
  cursor: not-allowed;
}

.provider-option input {
  accent-color: #496782;
}

.provider-option span {
  display: flex;
  flex-direction: column;
  gap: 3px;
  color: #333;
}

.provider-option small {
  color: #777;
  font-size: 12px;
}

.provider-error {
  margin: 8px 0 0;
  color: #e74c3c;
  font-size: 13px;
}

.quote-error {
  color: #e74c3c;
  font-size: 13px;
}

.payment-info {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #f0f7ff;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #333;
}

.subscription-agreement {
  padding: 12px 14px;
  background: #e0f4fc;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 12px;
  color: #26374E;
  line-height: 1.5;
}

.agreement-amount-info {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #26374E;
  font-weight: 500;
}

.info-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.pay-btn {
  width: 100%;
  padding: 16px 24px;
  background: linear-gradient(135deg, #496782, #5a7a99);
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;
}

.pay-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #3a5569, #4a6a88);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(73, 103, 130, 0.3);
}

.pay-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.back-link {
  text-align: center;
  margin-top: 16px;
}

.back-link a {
  color: #666;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.2s;
}

.back-link a:hover {
  color: #496782;
}

.payment-security {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #eee;
  font-size: 13px;
  color: #999;
}

.payment-result {
  background: #fff;
  border-radius: 16px;
  padding: 48px 32px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  text-align: center;
}

.result-icon {
  margin-bottom: 24px;
}

.payment-result h2 {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 12px 0;
}

.payment-result.error h2 {
  color: #F44336;
}

.payment-result p {
  font-size: 16px;
  color: #666;
  margin: 0 0 8px 0;
}

.retry-btn {
  display: inline-block;
  padding: 14px 32px;
  background: linear-gradient(135deg, #496782, #5a7a99);
  border: none;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 16px;
}

.retry-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(73, 103, 130, 0.3);
}

.back-btn {
  display: block;
  margin-top: 16px;
  color: #666;
  text-decoration: none;
  font-size: 14px;
}

.back-btn:hover {
  color: #496782;
}

@media (max-width: 768px) {
  .payment-container {
    padding: 20px 16px;
  }

  .payment-form {
    padding: 24px 20px;
  }
}
</style>
